import { useEffect, useState } from 'react'
import { fetchZabbixSettings, triggerZabbixSync, updateZabbixSettings } from '@/api/zabbix'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { formatDateTime } from '@/lib/formatAgent'

export function ZabbixSettingsCard({ embedded = false }) {
  const [baseUrl, setBaseUrl] = useState('')
  const [apiToken, setApiToken] = useState('')
  const [tokenHint, setTokenHint] = useState('')
  const [tokenConfigured, setTokenConfigured] = useState(false)
  const [lastSyncedAt, setLastSyncedAt] = useState(null)
  const [lastStatus, setLastStatus] = useState(null)
  const [lastError, setLastError] = useState('')
  const [lastHostCount, setLastHostCount] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [error, setError] = useState('')
  const [saved, setSaved] = useState(false)
  const [syncMessage, setSyncMessage] = useState('')

  function applySettings(data) {
    setBaseUrl(data.base_url || '')
    setTokenConfigured(Boolean(data.api_token_configured))
    setTokenHint(data.api_token_hint || '')
    setLastSyncedAt(data.last_synced_at || null)
    setLastStatus(data.last_status || null)
    setLastError(data.last_error || '')
    setLastHostCount(
      typeof data.last_host_count === 'number' ? data.last_host_count : null,
    )
    setApiToken('')
  }

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      setError('')
      try {
        const data = await fetchZabbixSettings()
        if (!cancelled) applySettings(data)
      } catch (err) {
        if (!cancelled) {
          setError(
            typeof err.response?.data?.detail === 'string'
              ? err.response.data.detail
              : 'Could not load Zabbix settings.',
          )
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [])

  async function handleSubmit(event) {
    event.preventDefault()
    setSaving(true)
    setError('')
    setSaved(false)
    setSyncMessage('')
    try {
      const updated = await updateZabbixSettings({
        base_url: baseUrl.trim(),
        api_token: apiToken.trim() || null,
      })
      applySettings(updated)
      setSaved(true)
    } catch (err) {
      setError(
        typeof err.response?.data?.detail === 'string'
          ? err.response.data.detail
          : 'Could not save Zabbix settings.',
      )
    } finally {
      setSaving(false)
    }
  }

  async function handleSync() {
    setSyncing(true)
    setError('')
    setSaved(false)
    setSyncMessage('')
    try {
      const result = await triggerZabbixSync()
      if (result.status === 'ok') {
        setSyncMessage(
          `Synced ${result.synced} host${result.synced === 1 ? '' : 's'}` +
            (result.removed ? ` and removed ${result.removed}.` : '.'),
        )
      } else {
        setError(result.reason || 'Zabbix sync did not complete.')
      }
      const data = await fetchZabbixSettings()
      applySettings(data)
    } catch (err) {
      setError(
        typeof err.response?.data?.detail === 'string'
          ? err.response.data.detail
          : 'Could not sync Zabbix hosts.',
      )
    } finally {
      setSyncing(false)
    }
  }

  const body = (
    <div className="space-y-4">
      {!embedded ? (
        <div>
          <h2 className="text-lg font-semibold text-foreground">Zabbix</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Host list only, used for the coverage report and the device-page link.
          </p>
        </div>
      ) : null}

      {error ? (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : null}
      {saved ? <p className="text-sm text-muted-foreground">Settings saved.</p> : null}
      {syncMessage ? <p className="text-sm text-muted-foreground">{syncMessage}</p> : null}

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <form onSubmit={handleSubmit} className="max-w-xl space-y-5">
          <div className="space-y-2">
            <Label htmlFor="zabbix-base-url">Zabbix URL</Label>
            <Input
              id="zabbix-base-url"
              value={baseUrl}
              onChange={(event) => setBaseUrl(event.target.value)}
              placeholder="http://100.85.148.42/zabbix"
              autoComplete="off"
            />
            <p className="text-xs text-muted-foreground">
              Base address only, without <span className="font-medium">zabbix.php</span>. The
              Dash server must be able to reach it, and browsers use the same address for Open
              in Zabbix.
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="zabbix-api-token">API token</Label>
            <Input
              id="zabbix-api-token"
              type="password"
              value={apiToken}
              onChange={(event) => setApiToken(event.target.value)}
              placeholder={tokenConfigured ? tokenHint || 'Token saved' : 'Paste a Zabbix API token'}
              autoComplete="new-password"
            />
            <p className="text-xs text-muted-foreground">
              {tokenConfigured
                ? 'A token is saved. Leave this blank to keep it.'
                : 'Create a token in Zabbix under User settings → API tokens.'}
            </p>
          </div>
          <p className="text-xs text-muted-foreground">
            Last sync:{' '}
            {lastSyncedAt
              ? `${formatDateTime(lastSyncedAt)}${lastStatus ? ` (${lastStatus})` : ''}${
                  lastHostCount != null ? ` · ${lastHostCount} hosts` : ''
                }`
              : 'not run yet'}
            {lastError ? `. ${lastError}` : ''}
          </p>
          <div className="flex flex-wrap gap-2">
            <Button type="submit" disabled={saving || syncing}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleSync}
              disabled={saving || syncing || !baseUrl.trim() || !tokenConfigured}
            >
              {syncing ? 'Syncing…' : 'Sync now'}
            </Button>
          </div>
        </form>
      )}
    </div>
  )

  return body
}
